import { libelleAppareil } from '@admin-pmvb/core'
import { estInstallee, estIos } from './plateforme'

// Notifications de l'appli installable (Web Push) côté navigateur : ce que l'appareil permet,
// l'abonnement demandé sur un geste (iOS l'exige, et seulement pour l'appli ajoutée à l'écran
// d'accueil), l'empreinte qui reconnaît « cet appareil » parmi ceux de la personne.

export type PriseEnCharge =
  | 'ok'
  /** iPhone ou iPad dans Safari : il faut d'abord ajouter l'appli à l'écran d'accueil. */
  | 'ios_a_installer'
  /** Pas de service worker (développement) : seulement dans l'appli déployée. */
  | 'sans_service_worker'
  | 'non_prise_en_charge'

export function priseEnCharge(): PriseEnCharge {
  if (typeof window === 'undefined') return 'non_prise_en_charge'
  const ios = estIos(navigator)
  if (ios && !estInstallee()) return 'ios_a_installer'
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window))
    return 'non_prise_en_charge'
  // Le service worker n'est enregistré qu'en production (enregistrement.ts).
  if (!import.meta.env.PROD) return 'sans_service_worker'
  return 'ok'
}

export function permission(): NotificationPermission | null {
  return typeof Notification === 'undefined' ? null : Notification.permission
}

function octetsBase64url(texte: string): Uint8Array<ArrayBuffer> {
  const b64 = texte
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(texte.length / 4) * 4, '=')
  const brut = atob(b64)
  const octets = new Uint8Array(new ArrayBuffer(brut.length))
  for (let i = 0; i < brut.length; i++) octets[i] = brut.charCodeAt(i)
  return octets
}

function memesOctets(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (!a) return false
  const x = new Uint8Array(a)
  return x.length === b.length && x.every((o, i) => o === b[i])
}

/** L'abonnement de ce navigateur, s'il existe (sans rien demander). */
export async function abonnementCourant(): Promise<PushSubscription | null> {
  if (priseEnCharge() !== 'ok') return null
  const enregistrement = await navigator.serviceWorker.getRegistration('/')
  return (await enregistrement?.pushManager.getSubscription()) ?? null
}

export type CorpsAbonnement = {
  endpoint: string
  cles: { p256dh: string; auth: string }
  libelle: string
}

export function corpsAbonnement(a: PushSubscription): CorpsAbonnement {
  const json = a.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }
  return {
    endpoint: json.endpoint ?? a.endpoint,
    cles: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    libelle: libelleAppareil(navigator.userAgent),
  }
}

/**
 * À appeler dans le gestionnaire du geste (clic) : la permission d'abord, sans rien attendre
 * avant (Safari perd le geste), puis l'abonnement chez le service du navigateur.
 */
export async function activerNotifications(clePublique: string): Promise<CorpsAbonnement> {
  const choix = await Notification.requestPermission()
  if (choix !== 'granted')
    throw new Error(
      choix === 'denied'
        ? 'Notifications refusées : les autoriser dans les réglages du téléphone, pour cette appli.'
        : 'Notifications non autorisées.',
    )
  const enregistrement = await navigator.serviceWorker.ready
  const cle = octetsBase64url(clePublique)
  let existant = await enregistrement.pushManager.getSubscription()
  // Clé VAPID changée sur le serveur : l'ancien abonnement ne recevra plus rien.
  if (existant && !memesOctets(existant.options.applicationServerKey, cle)) {
    await existant.unsubscribe()
    existant = null
  }
  const abonnement =
    existant ??
    (await enregistrement.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: cle,
    }))
  return corpsAbonnement(abonnement)
}

/** Empreinte SHA-256 de l'adresse, comme celle que rend l'API pour chaque appareil. */
export async function empreinteAbonnement(a: PushSubscription): Promise<string> {
  const condense = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(a.endpoint))
  return [...new Uint8Array(condense)].map((o) => o.toString(16).padStart(2, '0')).join('')
}
